import AuthenticatedLayout from '@/Layouts/Auth/AuthenticatedLayout';
import {Head, router, useRemember} from '@inertiajs/react';
import {
    ExtractionJob,
    ExtractorProps,
    Folder,
    JobDetails,
    PageProps,
    PaginatedExtractionJob,
    PersistedExtractionJob,
    UserSettings,
    TranslationSummary,
    JobTableItem
} from '@/types';
import {ExtractionJobStatus} from '@/types/enums';
import ExtractionJobsTable from "@/Pages/Dashboard/Partials/ExtractionJobsTable";
import React, {useEffect, useMemo, useState} from "react";
import toast from "react-hot-toast";
import UploadModal, {RemoteUpload} from "@/Pages/Dashboard/Upload/UploadModal";
import {
    duplicateUpload, isJobRunning,
    listenForExtractionJobUpdates,
    retryJob,
    startJobs
} from "@/services/extractionJobManager";
import MediaFileInput from "@/Components/MediaFileInput";
import {bytesToSize} from "@/utils/textFormatting";
import moment from "moment";
import Credits from "@/Pages/Dashboard/Partials/Credits";
import MobileMenuCredits from "@/Pages/Dashboard/Partials/MobileMenuCredits";
import {AnalyticsEvent, sendEvent} from "@/services/analytics";
import RemoteUploadModal from "@/Pages/Dashboard/Partials/RemoteUploadModal";
import usePaginatedSource, { PaginatedSource } from "@/hooks/usePaginatedSource";
import axios from "axios";
import FeedbackModal from "@/Pages/Dashboard/Feedback/FeedbackModal";
import TranslateModal from "@/Pages/Dashboard/Translate/TranslateModal";
import {bulkDownloadSubtitles, downloadSubtitles} from "@/services/subtitleService";
import {TranslationMethod} from "@/types/enums";
import EmptyInboxIcon from "@/Components/Icons/EmptyInboxIcon";
import TrashIcon from "@/Components/Icons/TrashIcon";
import debounce from "debounce";
import ExportModal from "@/Pages/Subtitle/Partials/ExportModal";
import WorkflowFormModal from "@/Pages/Workflow/Components/WorkflowFormModal";
import FolderSidebar from "@/Pages/Dashboard/Folders/FolderSidebar";
import FolderRenameModal from "@/Pages/Dashboard/Folders/FolderRenameModal";
import FolderCreateModal from "@/Pages/Dashboard/Folders/FolderCreateModal";
import {folderService} from "@/services/folderService";
import {useConfirm} from "@/Components/ConfirmDialog";
import {useDarkMode} from "@/hooks/useDarkMode";
import {useIsClient} from "@/hooks/useIsClient";

type DashboardPageProps = {
    totalJobsWithDeleted: number,
    totalJobs: number,
    extractorParams: ExtractorProps,
    csrf_token: string,
    creditBalance: number,
    userSettings: UserSettings,
    folders: Folder[],
    currentFolderId: string | null
}

export default function Dashboard({
    auth,
    extractorParams,
    creditBalance,
    totalJobsWithDeleted,
    userSettings,
    totalJobs: initialTotalJobs,
    folders: initialFolders,
    currentFolderId
}: PageProps<DashboardPageProps>) {
    const confirm = useConfirm();
    const isClient = useIsClient();
    const [isNewUser, setIsNewUser] = useState(!totalJobsWithDeleted);
    const [totalJobs, setTotalJobs] = useState(initialTotalJobs);
    const [credits, setCredits] = useState(creditBalance);
    const [selectedFiles, setSelectedFiles] = useState<File[]>([])
    const [remoteUpload, setRemoteUpload] = useState<RemoteUpload | null>(null)
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [showRemoteUploadModal, setShowRemoteUploadModal] = useState(false);
    const [showTranslationTip, setShowTranslationTip] = useState(false);
    const [bulkDownloadJobs, setBulkDownloadJobs] = useState<PaginatedExtractionJob[]>([]);
    const [feedbackJob, setFeedbackJob] = useState<PersistedExtractionJob | null>(null);
    const [bulkTranslateJobs, setBulkTranslateJobs] = useState<PersistedExtractionJob[]>([]);
    const [searchValue, setSearchValue] = useState('')
    const [folders, setFolders] = useState<Folder[]>(initialFolders);
    const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
    const [showRenameFolderModal, setShowRenameFolderModal] = useState(false);
    const [selectedFolder, setSelectedFolder] = useState<Folder | null>(null);
    const isDark = useDarkMode();

    const paginatedJobs = usePaginatedSource<PaginatedExtractionJob>(
        'job.list',
        null,
        {searchValue, folderId: currentFolderId}
    )

    useEffect(() => {
        setCredits(creditBalance);
    }, [creditBalance]);

    useEffect(() => {
        if (paginatedJobs.responseData.folders) {
            const responseFolders = paginatedJobs.responseData.folders as Folder[];
            setFolders(responseFolders);
        }
    }, [paginatedJobs.responseData.folders]);

    useEffect(() => {
        if (currentFolderId && !folders.find(f => f.id === currentFolderId)) {
            toast.error('Folder not found');
            router.visit(route('dashboard'));
        }
    }, [currentFolderId, folders]);

    const currentFolder = currentFolderId ? folders.find(f => f.id === currentFolderId) : null;
    const folderName = currentFolder?.name || 'All Files';

    const tableItems = useMemo((): JobTableItem[] => {
        if (paginatedJobs.isLoading) {
            return [];
        }

        if (currentFolderId !== null || paginatedJobs.pagination.currentPage > 1) {
            return paginatedJobs.items;
        }

        const filteredFolders = folders
            .filter(folder => !searchValue || folder.name.toLowerCase().includes(searchValue.toLowerCase()));

        return [...filteredFolders, ...paginatedJobs.items];
    }, [currentFolderId, searchValue, paginatedJobs.isLoading, paginatedJobs.items, paginatedJobs.pagination.currentPage, folders]);

    const [currentPage, setCurrentPage] = useRemember(1, 'currentPage');

    const [dragOverFolder, setDragOverFolder] = useState<string | null>(null);
    const [isDragging, setIsDragging] = useState(false);

    const handleSearch = useMemo(() =>
        debounce((value: string) => setSearchValue(value), 400), [])

    const bulkDownloadLanguages = useMemo(() => {
        if (!bulkDownloadJobs.length) return [];
        const jobLanguages = bulkDownloadJobs.map(job => job.output_languages);
        const shared = jobLanguages[0].filter(lang => jobLanguages.every(arr => arr.includes(lang)));
        if(shared.length === 1 && jobLanguages.every(arr => arr[0] === shared[0])) {
            return [];
        }
        if(jobLanguages.some(arr => arr.length !== shared.length)) {
            shared.unshift('original');
        }
        return shared;
    }, [bulkDownloadJobs]);

    useEffect(() => {
        if (selectedFiles.length) {
            setShowUploadModal(true);
        }
    }, [selectedFiles]);

    useEffect(() => {
        setCurrentPage(paginatedJobs.pagination.currentPage)
    }, [paginatedJobs.pagination.currentPage]);

    useEffect(() => {
        if (currentPage !== paginatedJobs.pagination.currentPage) {
            paginatedJobs.setPage(currentPage, true);
        }
    }, []);

    useEffect(() => {
        const runningJobs = paginatedJobs.items.filter(job => isJobRunning(job.status));
        const listeners = runningJobs.map(job => {
            let completed = false;
            return listenForExtractionJobUpdates({
                job_name: job.job_name!,
                method: job.method,
                video_duration: job.file_duration!,
                started_at: moment(job.started_at || job.created_at).valueOf(),
                on_update: (progress) => {
                    paginatedJobs.updateItems(
                        prev => prev.map(j => j.job_name === job.job_name ? {...j, progress} : j)
                    )
                },
                on_complete: () => {
                    if(!completed) {
                        completed = true;
                        handleJobComplete(job)
                    }
                },
                on_error: (err) => {
                    toast.error(err.message);
                    paginatedJobs.reloadItems({update: job.id});
                }
            })
        })
        return () => {
            listeners.forEach(el => el.disconnect());
        }
    }, [paginatedJobs.lastLoadAt]);

    useEffect(() => {
        if (paginatedJobs.error) {
            toast.error(paginatedJobs.error);
        }
    }, [paginatedJobs.error]);

    useEffect(() => {
        if (paginatedJobs.initialized) {
            setTotalJobs(paginatedJobs.pagination.total)
        }
    }, [paginatedJobs.pagination.total, paginatedJobs.initialized]);

    const onFilesSelected = (files: File[]) => {
        const subtitlePattern = /\.(srt|vtt|ass|json)$/i;
        const subtitles = files.filter(file => file.name.match(subtitlePattern));
        const videos = files.filter(file => !file.name.match(subtitlePattern));
        if (subtitles.length) {
            toast.promise(uploadSubtitles(subtitles, currentFolderId), {
                loading: 'Uploading subtitles...',
                success: 'Subtitles uploaded successfully!',
                error: err => err.response?.data?.message || 'Failed to upload subtitles'
            })
        } else {
            setSelectedFiles(videos);
        }
    }

    const uploadSubtitles = (files: File[], folderId: string|null) => {
        return axios.post(route('subtitle.upload'), {
            files,
            folderId
        }, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        }).then(response => {
            paginatedJobs.reloadItems({update: true});
        })
    }

    const handleRetry = async (job: PersistedExtractionJob) => {
        if(job.status === ExtractionJobStatus.Failed) {
            await retryFailedJob(job);
        } else {
            await retryWithDifferentSettings(job);
        }
    }

    const retryFailedJob = async (job: PersistedExtractionJob) => {
        await toast.promise(retryJob(job), {
            loading: 'Submitting',
            success: () => {
                paginatedJobs.reloadItems();
                return "Job Restarted"
            },
            error: err => {
                return err.message || 'An error occurred while retrying the job';
            }
        })
    }

    const retryWithDifferentSettings = async (job: PersistedExtractionJob) => {
        try {
            toast.loading('Preparing upload');
            const uploadId = await duplicateUpload(job);
            const upload: RemoteUpload = {
                id: uploadId,
                fileName: job.file_name,
                fileSize: job.file_size,
                progress: 1,
                options: {},
                url: ''
            }
            setRemoteUpload(upload);
            toast.dismiss();
        } catch (e: any) {
            toast.dismiss();
            toast.error(e.message);
        }
    }

    const handleBulkDownload = async (
        format: string,
        languages: string[],
        options: Record<string, any>
    ) => {
        try {
            if (bulkDownloadJobs.length === 1 && languages.length < 2) {
                await downloadSubtitles(
                    bulkDownloadJobs[0],
                    languages[0] || null,
                    format,
                    options
                )
            } else {
                await bulkDownloadSubtitles(
                    bulkDownloadJobs.map(job => job.id),
                    languages,
                    format,
                    options
                )
            }
        } catch (err: any) {
            const msg = err.message || 'Failed to download subtitles';
            toast.error(msg);
        }
    }

    const handleJobComplete = (job: PersistedExtractionJob) => {
        toast('Extraction complete!', {icon: '🎉'});
        paginatedJobs.reloadItems({completed: job.id}).then(jobs => {
            const outputFileSize = jobs?.[0]?.subtitle_count || 0; // this maybe incorrect if currentPage is not 1
            if (outputFileSize > 0) {
                if (isNewUser) {
                    setShowTranslationTip(true);
                }
                sendEvent(AnalyticsEvent.EXTRACTION_SUCCESS, {method: job.method});
                if (userSettings.downloadOnComplete) {
                    downloadSubtitles(job).catch(e => {});
                }
            }
            setIsNewUser(false);
        })
    }

    const handleBulkTranslateComplete = async (
        summary: TranslationSummary,
        method: TranslationMethod
    ) => {
        if(!summary.hasFailures) {
            const languages = Array.from(new Set(
                summary.results.flatMap(r => r.successful.map(s => s.language))
            ));
            const jobIds = summary.results.map(r => r.jobId);
            bulkDownloadSubtitles(jobIds, languages).catch(() => {});
        }
        setBulkTranslateJobs([]);
    }

    const refreshCredits = (): Promise<number> => {
        return new Promise((resolve) => {
            router.reload({
                onSuccess: (page) => {
                    const balance = page.props.creditBalance as number;
                    setCredits(balance);
                    resolve(balance);
                }
            })
        });
    }

    async function initJobs(
        uploads: JobDetails[]
    ): Promise<void> {
        try {
            if (!uploads.length) {
                return;
            }

            const balance = await startJobs(uploads);
            setCredits(balance);
            toast.success('Extraction started!');
            sendEvent(AnalyticsEvent.EXTRACTION, {
                method: uploads[0].method,
                batch_size: uploads.length
            });
            paginatedJobs.setPage(1, true);
            setShowUploadModal(false);
            setSelectedFiles([]);
        } catch (err: any) {
            throw err;
        }
    }

    useEffect(() => {
        if (remoteUpload) {
            setShowRemoteUploadModal(false)
            setShowUploadModal(true)
        }
    }, [remoteUpload])

    const handleMoveToFolder = async (jobIds: number[], folderId: string | null) => {
        try {
            await folderService.moveJobs({ job_ids: jobIds, folder_id: folderId });
            const message = `File${jobIds.length > 1 ? 's' : ''} moved`
            toast.success(message);
            paginatedJobs.reloadItems();
        } catch (error: any) {
            const message = error.response?.data?.message || 'Failed to move jobs';
            toast.error(message);
        }
    };

    const handleFolderDragOver = (e: React.DragEvent, folderId: string | null) => {
        e.preventDefault();
        setDragOverFolder(folderId);
    };

    const handleFolderDragLeave = () => {
        setDragOverFolder(null);
    };

    const handleFolderDrop = (e: React.DragEvent, folderId: string | null) => {
        e.preventDefault();
        const jobId = e.dataTransfer.getData('text/plain');
        if (jobId) {
            handleMoveToFolder([parseInt(jobId)], folderId);
        }
        setDragOverFolder(null);
    };

    const handleFolderDelete = async (folder: Folder) => {
        const jobCount = folder.extraction_jobs_count;

        if (await confirm({
            title: 'Delete Folder',
            message: `Are you sure you want to delete the folder
                "${folder.name}"${jobCount ? ` and all ${jobCount} files within it` : ''}? This action cannot be undone.`
        })) {
            setFolders(prev => prev.filter(f => f.id !== folder.id));
            try {
                await folderService.deleteFolder(folder.id);
                toast.success(jobCount ? 'Folder and all files deleted successfully' : 'Folder deleted successfully')
            } catch (error: any) {
                toast.error(error.response?.data?.message || 'Failed to delete folder');
            } finally {
                void paginatedJobs.reloadItems({}, {silent: true});
            }
        }
    };

    const handleFolderCreated = (folder: Folder) => {
        setFolders(prev => [...prev, folder].sort((a, b) => a.name.localeCompare(b.name)));
    };

    const handleFolderRename = (folder: Folder) => {
        setSelectedFolder(folder);
        setShowRenameFolderModal(true);
    };

    const handleFolderRenamed = (updatedFolder: Folder) => {
        setFolders(prev => prev.map(f => f.id === updatedFolder.id ? {...f, ...updatedFolder} : f).sort((a, b) => a.name.localeCompare(b.name)));
        setShowRenameFolderModal(false);
        setSelectedFolder(null);
        toast.success('Folder renamed');
    };

    return (
        <AuthenticatedLayout
            mobileMenuExtras={closeMenu => (
                <MobileMenuCredits
                    credits={credits}
                    prices={extractorParams.pricing}
                    onBuyClick={closeMenu}
                />
            )}
            header={
                <header className="bg-white dark:bg-gray-800 shadow hidden md:block">
                    <div className="container mx-auto py-4 lg:py-6 px-4 sm:px-6 lg:px-8">
                        <h2 className="font-semibold text-xl text-gray-800 dark:text-gray-200 leading-tight">Dashboard</h2>
                    </div>
                </header>}
        >
            <Head title="Dashboard"/>
            <FeedbackModal
                job={feedbackJob}
                onClose={() => setFeedbackJob(null)}
                onSubmitted={() => {
                    toast.success('Feedback submitted!')
                    setFeedbackJob(null)
                }}
            />
            <ExportModal
                isBulk={bulkDownloadJobs.length > 1}
                show={bulkDownloadJobs.length > 0}
                onClose={() => setBulkDownloadJobs([])}
                availableLanguages={bulkDownloadLanguages}
                onDownload={handleBulkDownload}
                initialUpload={null}
                isFreeTrial={auth.user.is_trial}
            />
            <UploadModal
                ignoreSoftcodedSubtitles={userSettings.ignoreSoftcodedSubtitles}
                autoSubmit={userSettings.autoSubmitExtraction}
                initialFiles={selectedFiles}
                initialUpload={remoteUpload}
                show={showUploadModal}
                currentFolder={currentFolder}
                isPaidUser={!auth.user.is_trial}
                credits={credits}
                onCreditRefresh={refreshCredits}
                extractorParams={extractorParams}
                onClose={() => {
                    setShowUploadModal(false);
                    setSelectedFiles([])
                    setRemoteUpload(null)
                }}
                submit={initJobs}
            />
            <TranslateModal
                bulk={true}
                isFreeTrial={auth.user.is_trial}
                show={bulkTranslateJobs.length > 0}
                onTranslateComplete={handleBulkTranslateComplete}
                jobs={bulkTranslateJobs}
                onClose={() => {
                    setBulkTranslateJobs([]);
                }}
            />
            <RemoteUploadModal
                show={showRemoteUploadModal}
                onClose={() => {
                    setShowRemoteUploadModal(false);
                    setRemoteUpload(null)
                }}
                onUploadComplete={upload => {
                    setRemoteUpload(upload)
                    toast.success('Video imported successfully!')
                }}
            />
            <div className="container mx-auto">
                <div className="flex flex-col lg:flex-row md:py-8">

                    <div className="hidden lg:block lg:w-1/4 2xl:w-1/5 m-4 space-y-4 xl:space-y-6">
                        <FolderSidebar
                            folders={folders}
                            selectedFolderId={currentFolderId}
                            onMoveToFolder={handleMoveToFolder}
                            dragOverFolder={dragOverFolder}
                            isDragging={isDragging}
                            onFolderDragOver={handleFolderDragOver}
                            onFolderDragLeave={handleFolderDragLeave}
                            onFolderDrop={handleFolderDrop}
                            onFolderDelete={handleFolderDelete}
                            onFolderCreated={handleFolderCreated}
                            onFolderRenamed={(updatedFolder) => setFolders(prev => prev.map(f => f.id === updatedFolder.id ? {...f, ...updatedFolder} : f).sort((a, b) => a.name.localeCompare(b.name)))}
                        />

                        {/* Credits */}
                        <div className="p-4 py-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm sm:p-6">
                            <Credits
                                user={auth.user}
                                prices={extractorParams.pricing}
                                credits={credits}
                            />
                        </div>
                    </div>


                    <div className="lg:w-3/4 2xl:w-4/5 md:m-4">
                        <div className=" pb-1 md:pb-0 pt-6 bg-white dark:bg-gray-800 md:border border-gray-200 dark:border-gray-700 md:rounded-lg shadow-sm">
                            <div className="mb-4 px-6 lg:mb-0">
                                {currentFolderId ? (
                                    <h5 className="mb-2 text-xl font-bold text-gray-900 dark:text-gray-100">
                                        <button
                                            onClick={() => {
                                                router.visit(route('dashboard'), { preserveState: true, preserveScroll: true });
                                            }}
                                            className="text-gray-400 dark:text-gray-500 hover:text-gray-800 dark:hover:text-gray-200"
                                        >
                                            All Files
                                        </button>
                                        <span className="mx-2 text-gray-400 dark:text-gray-500">/</span>
                                        <button
                                            onClick={() => currentFolder && handleFolderRename(currentFolder)}
                                            className="hover:bg-gray-100 dark:hover:bg-gray-700 rounded px-1 py-1 -mx-1 -my-1 transition-colors"
                                        >
                                            {folderName}
                                        </button>
                                    </h5>
                                ) : (
                                    <h5 className="mb-2 hidden md:block text-xl font-bold text-gray-900 dark:text-gray-100">
                                        {folderName}
                                    </h5>
                                )}
                            </div>
                            <div className={!isNewUser ? 'py-4' : 'py-6'}>
                                <MediaFileInput
                                    canSelectUrl={!auth.user.is_trial}
                                    multiple={true}
                                    onSelectUrl={() => setShowRemoteUploadModal(true)}
                                    maxSize={extractorParams.maxFileSize}
                                    onFileSizeExceeded={() => {
                                        toast.error(`File is too large. Max file size is ${bytesToSize(extractorParams.maxFileSize)}`);
                                    }}
                                    onFilesRejected={(err: string) => {
                                        toast.error(err)
                                    }}
                                    onFilesSelected={onFilesSelected}/>
                            </div>

                            {
                                (totalJobs > 0 || searchValue) ? <ExtractionJobsTable
                                    onSearch={handleSearch}
                                    onBulkDownload={setBulkDownloadJobs}
                                    onBulkTranslate={(jobs) => setBulkTranslateJobs(jobs)}
                                    paginatedJobs={paginatedJobs}
                                    tableItems={tableItems}
                                    onRetry={handleRetry}
                                    initialRows={totalJobs}
                                    onFeedback={job => setFeedbackJob(job)}
                                    showTranslateTip={showTranslationTip}
                                    folders={folders}
                                    currentFolder={currentFolder || null}
                                    onMoveToFolder={handleMoveToFolder}
                                    onFolderDelete={handleFolderDelete}
                                    onFoldersRemoved={(ids) => setFolders(prev => prev.filter(f => !ids.includes(f.id)))}
                                    onCreateFolder={() => setShowCreateFolderModal(true)}
                                    onDragStart={() => setIsDragging(true)}
                                    onDragEnd={() => {
                                        setIsDragging(false);
                                        setDragOverFolder(null);
                                    }}
                                    onFolderDrop={handleFolderDrop}
                                /> : null
                            }
                            {
                                (!totalJobs && !searchValue) ? <div>
                                    <hr className="my-12 bg-gray-900 dark:bg-gray-700 dark:border-gray-600"/>
                                    <div className="text-center  px-10 mb-28 mt-38">
                                        <div className="dark:opacity-50">
                                            {isClient && <EmptyInboxIcon dark={isDark}/>}
                                        </div>
                                        <p className="text-xl text-gray-400 dark:text-gray-400">Extracted files will appear here</p>
                                    </div>
                                </div> : null
                            }
                        </div>
                    </div>
                </div>
            </div>


            <div className="hidden md:block py-2"></div>

            <FolderCreateModal
                show={showCreateFolderModal}
                onClose={() => setShowCreateFolderModal(false)}
                onFolderCreated={(folder) => {
                    handleFolderCreated(folder);
                    setShowCreateFolderModal(false);
                }}
            />

            <FolderRenameModal
                show={showRenameFolderModal}
                folder={selectedFolder}
                onClose={() => {
                    setShowRenameFolderModal(false);
                }}
                onFolderRenamed={handleFolderRenamed}
            />

        </AuthenticatedLayout>
    );
}
